import React, { useState, useEffect } from 'react'
import { Input, Box, VStack } from '@chakra-ui/react'

interface SearchDropdownProps {
  data: string[]
  placeholder: string
  strictlyUsePlaceholder?: boolean
  value?: string
  onSearchChange?: (query: string) => void
  minSearchLength?: number
  formatDropdownItem: (result: string) => React.ReactNode
  formatSelectedItem: (result: string) => string
  action: (result: string) => void
}

export const SearchDropdown: React.FC<SearchDropdownProps> = ({
  data,
  placeholder,
  strictlyUsePlaceholder = false,
  value,
  onSearchChange,
  minSearchLength = 0,
  formatDropdownItem,
  formatSelectedItem,
  action
}) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<string[]>(data)
  const [selected, setSelected] = useState<string | null>(null)

  useEffect(() => {
    setSelected(value ?? null)
  }, [value])

  useEffect(() => {
    if (searchQuery.length < minSearchLength) {
      setResults([])
      return
    }

    if (onSearchChange) {
      setResults(data)
      return
    }

    setResults(data.filter((item) => item.toLowerCase().includes(searchQuery.toLowerCase())))
  }, [data, minSearchLength, onSearchChange, searchQuery])

  const shouldShowResults =
    searchQuery.length > 0 && searchQuery.length >= minSearchLength && results.length > 0

  return (
    <Box position='relative'>
      <Input
        placeholder={strictlyUsePlaceholder || !selected ? placeholder : selected}
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value)
          onSearchChange?.(e.target.value)
        }}
        focusBorderColor='teal.500'
        sx={{
          '&::placeholder': {
            color: 'gray.800'
          }
        }}
      />
      {shouldShowResults && (
        <Box
          position='absolute'
          top='100%'
          left={0}
          zIndex={1000}
          bg='white'
          boxShadow='0 2px 4px rgba(0,0,0,0.1)'
          borderRadius='4px'
          width='100%'
          overflow='auto'
        >
          <VStack spacing={0} align='stretch'>
            {results.length > 0 &&
              results.slice(0, 10).map((result, index) => (
                <Box
                  key={index}
                  p={2}
                  cursor='pointer'
                  _hover={{ bg: 'gray.100' }}
                  onClick={() => {
                    action(result)
                    setSearchQuery('')
                    onSearchChange?.('')
                    setSelected(formatSelectedItem(result))
                  }}
                >
                  {formatDropdownItem(result)}
                </Box>
              ))}
          </VStack>
        </Box>
      )}
    </Box>
  )
}
